/**
 * Primitive design tokens — values authored in Figma (Vayu — Design System
 * Unified, file `DXs9IGKZDu39CKmytmqPdr`, Mode 1) and exported to JSON. This
 * file is the TS mirror of those primitives, plus the dimensions Figma doesn't
 * yet export (radius, elevation, motion, borderWidth — carried from prior crawl).
 *
 * Naming convention (3-way):
 *   TS access:        color.vayuBlue[500]
 *   CSS variable:     --color-vayu-blue-500
 *   Storybook label:  vayuBlue/500
 *
 * Components must consume via the MUI theme (`theme.palette.*`,
 * `theme.spacing(...)`, `theme.shape.*`), never import primitives directly.
 */

export const color = {
  /** Body text, borders, neutral surfaces, dark inverse surface at 950. */
  neutrals: {
    25: '#FAFBFE',
    50: '#F5F6FA',
    75: '#F0F0F5',
    100: '#E8EBF2',
    200: '#D6DBE7',
    300: '#C0C7D4',
    400: '#A4ADBE',
    450: '#9CA3AF',
    500: '#7E8796',
    550: '#6B7280',
    600: '#5F6978',
    /** Figma neutral text — e.g. usage table row label. */
    650: '#374151',
    700: '#2D3949',
    /** Near-black body text — usage summary values. */
    725: '#323232',
    800: '#152232',
    /** Near-black heading text — usage modal title. */
    825: '#1a1a1a',
    850: '#292D32',
    900: '#020F20',
    950: '#01060D',
  },
  /** Destructive / error. */
  alert: {
    50: '#FEEBEE',
    100: '#FFDDE3',
    200: '#FFD0D8',
    300: '#FFB3C0',
    400: '#FD99AA',
    500: '#FF7D93',
    600: '#FF627D',
    700: '#FF4666',
    800: '#FF2E57',
    900: '#EE2049',
    950: '#DD0038',
  },
  /** Positive / approval. */
  success: {
    50: '#DFFAE5',
    100: '#C4F6D0',
    200: '#A7EBB7',
    300: '#87DE9C',
    400: '#65CF7E',
    500: '#53C36E',
    600: '#42AE5C',
    700: '#2C9746',
    800: '#1E7E35',
    900: '#126C28',
    950: '#175F28',
    /** Figma White Lilac — growth chip background. */
    badgeBg: '#E8F5E9',
    /** Figma Sea Green — growth chip text. */
    badgeText: '#2E7D32',
  },
  /** Info / neutral notification. */
  info: {
    50: '#E6F2FF',
    100: '#CFE6FF',
    200: '#B5D8FF',
    300: '#9BCBFF',
    400: '#79B6F9',
    500: '#5EA3EF',
    600: '#3788E6',
    700: '#3079D7',
    800: '#1D68C8',
    900: '#0953B1',
    950: '#05489C',
  },
  /**
   * Brand action ramp. `vayuBlue[950]` (#07105F) is the action navy used for
   * primary buttons (Button-primary-idle aliases here). `vayuBlue[500]`
   * (#2953FF) is the bright focus / info accent. The MUI palette pins
   * `primary.main` to `vayuBlue[950]`.
   */
  vayuBlue: {
    50: '#E7F2FF',
    100: '#D3E8FF',
    200: '#B0D2FF',
    300: '#81B3FF',
    400: '#5083FF',
    500: '#2953FF',
    600: '#0521FF',
    700: '#001CFF',
    800: '#011BE6',
    900: '#0C21A3',
    950: '#07105F',
    /** Figma Azure Radiance — focus accent ring. */
    accentFocus: '#0075FF',
    /** Figma blue/30 "Smalt" — interactive/selected action blue (selected toggle, links). */
    action: '#032497',
    /** Subtle azure fill paired with `action` text — inline info callouts (e.g. the installments banner). */
    actionSubtle: '#EAF2FE',
  },
  /** Secondary accent (charts, decorative). */
  turquoise: {
    50: '#EDFFFC',
    100: '#C3FFF9',
    200: '#87FFF6',
    300: '#42FFF1',
    400: '#23F6E7',
    /** Chart series color — teal bar fill (Figma chart palette entry 3). */
    425: '#78D6DC',
    500: '#00D8CB',
    600: '#00AFA8',
    700: '#1F7A87',
    800: '#176873',
    900: '#0F5861',
    950: '#003738',
  },
  /** Tertiary accent (avatars, decorative). */
  purple: {
    50: '#F4F2FF',
    100: '#EBE7FF',
    200: '#D8D2FF',
    300: '#BDAEFF',
    400: '#9C80FF',
    /** Chart series color — violet/orchid bar fill (Figma chart palette entry 8). */
    425: '#C265EC',
    500: '#7E4CFF',
    /** Chart series color — periwinkle bar fill (Figma chart palette entry 2). */
    450: '#697DFC',
    600: '#6F28FF',
    700: '#6116EB',
    800: '#5613D1',
    900: '#4411A1',
    950: '#27076E',
  },
  /** Caution / pending. */
  orange: {
    50: '#FEF9EE',
    100: '#FDF1D7',
    200: '#FADEAE',
    300: '#F4BA61',
    400: '#F1A346',
    500: '#ED8822',
    600: '#DE6F18',
    700: '#B85416',
    800: '#934419',
    900: '#763918',
    950: '#401B0A',
  },
  /** Base reserved tokens (Figma Base/White, Base/Black). */
  base: {
    white: '#FFFFFF',
    black: '#000000',
  },
} as const;

/**
 * Brand gradients (Figma: `Gradient/Default-Vayu-Gradient`,
 * `Gradient/Light-Vayu-Gradient`). Linear gradients with explicit angles
 * and stops as Figma authored them.
 */
export const gradient = {
  vayuDefault: 'linear-gradient(49.48deg, #0ACDA7 5.69%, #0F53F2 102.8%)',
  vayuLight: 'linear-gradient(76.56deg, #DEE8FF 0.79%, #D9F3EE 107.23%)',
} as const;

/**
 * 8-pt spacing scale (Figma Variables, 12 steps). MUI's `theme.spacing(n)`
 * is configured with factor 8 in `spacing.ts`, so `theme.spacing(N)` returns
 * the value at index N here.
 *
 * Index 0 is the synthetic zero step (not in Figma; MUI returns 0px).
 */
export const space = [0, 8, 16, 24, 32, 40, 48, 56, 64, 72, 80, 88, 96] as const;

/**
 * Chart series palette — 10 ordered colors for bar/line chart series.
 * Index into this array by series position; wrap with modulo for > 10 series.
 * Indices 4 and 5 are intentionally identical (`#1D68C8`) to match the
 * source design's visual-parity requirement.
 *
 * Usage: `chartSeries[seriesIndex % chartSeries.length]`
 */
export const chartSeries = [
  color.vayuBlue[300],    // 0  #81B3FF — periwinkle blue
  color.purple[450],      // 1  #697DFC — slate periwinkle
  color.turquoise[425],   // 2  #78D6DC — teal
  color.orange[300],      // 3  #F4BA61 — amber
  color.info[800],        // 4  #1D68C8 — cobalt blue
  color.info[800],        // 5  #1D68C8 — cobalt blue (duplicate for visual parity)
  color.info[100],        // 6  #CFE6FF — pale sky
  color.purple[425],      // 7  #C265EC — orchid
  color.alert[200],       // 8  #FFD0D8 — blush
  color.success[300],     // 9  #87DE9C — mint
] as const;

/** Border radius. Unchanged from prior theme until Figma surfaces specifics. */
export const radius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

/** Border widths. Unchanged. */
export const borderWidth = {
  none: 0,
  thin: 1,
  default: 2,
  thick: 3,
} as const;

/**
 * Type ramp from Figma typography page (sizes 12, 14, 16, 20, 24, 28, 36, 48,
 * 56). The half-step sizes (12.5, 13, 13.5) are intermediate slots Figma uses
 * for input-row typography — keep them on the ramp so component variants can
 * reference a token instead of a raw px literal.
 */
export const fontSize = {
  11: 11,
  '11_5': 11.5,
  12: 12,
  '12_5': 12.5,
  13: 13,
  '13_5': 13.5,
  14: 14,
  16: 16,
  20: 20,
  24: 24,
  28: 28,
  36: 36,
  48: 48,
  56: 56,
} as const;

/** Font weights observed in Figma (400 / 500 / 600 / 700). */
export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/**
 * Line heights from Figma, in px. Pairs with `fontSize` per variant
 * in `typography.ts`. Storybook story renders them as CSS strings.
 */
export const lineHeight = {
  16: '16px',
  '16_1': '16.1px',
  '17_5': '17.5px',
  20: '20px',
  '20_13': '20.13px',
  23: '23px',
  24: '24px',
  26: '26px',
  30: '30px',
  36: '36px',
  48: '48px',
  57: '57px',
  60: '60px',
  64: '64px',
} as const;

/**
 * Font families. Inter is the canonical family per Figma. `sans` and
 * `display` resolve to the same stack today but stay as separate slots
 * so a future rebrand can introduce a distinct heading face.
 */
export const fontFamily = {
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
  display: '"Inter", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
} as const;

/** Box-shadow elevations. Unchanged. */
export const elevation = {
  0: 'none',
  1: 'rgba(13, 27, 39, 0.03) 0px 1px 2px 0px',
  2: 'rgba(13, 27, 39, 0.04) 0px 1px 3px 0px, rgba(13, 27, 39, 0.03) 0px 1px 2px 0px',
  3: 'rgba(13, 27, 39, 0.03) 0px 2px 8px -1px, rgba(13, 27, 39, 0.03) 0px 1px 5px 0px',
  4: 'rgba(0, 0, 0, 0.08) 0px 4px 8px 0px, rgba(0, 0, 0, 0.04) 0px 2px 4px 0px',
  6: 'rgba(0, 0, 0, 0.10) 0px 6px 16px 0px, rgba(0, 0, 0, 0.06) 0px 3px 6px 0px',
  8: 'rgba(0, 0, 0, 0.14) 0px 4px 9px 0px',
  12: 'rgba(0, 0, 0, 0.16) 0px 12px 24px 0px, rgba(0, 0, 0, 0.08) 0px 4px 8px 0px',
  16: 'rgba(0, 0, 0, 0.18) 0px 16px 32px 0px, rgba(0, 0, 0, 0.10) 0px 6px 12px 0px',
  24: 'rgba(0, 0, 0, 0.22) 0px 24px 48px 0px, rgba(0, 0, 0, 0.12) 0px 8px 16px 0px',
} as const;

/** Motion durations and easings. Unchanged. */
export const duration = {
  fast: 150,
  base: 200,
  slow: 300,
} as const;

export const easing = {
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  decelerate: 'cubic-bezier(0.4, 0, 0.6, 1)',
  ease: 'ease',
} as const;

export type ColorRamp = Exclude<keyof typeof color, 'base'>;
export type ColorTint = keyof (typeof color)['neutrals'];
export type SpaceStep = (typeof space)[number];
export type RadiusKey = keyof typeof radius;
export type FontSizeKey = keyof typeof fontSize;
export type FontWeightKey = keyof typeof fontWeight;
export type GradientKey = keyof typeof gradient;
